import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import useDefaultModal from '../hooks/useDefaultModal';
import DefaultModal from '../modals/DefaultModal';
import useProgramaSituacao from '../hooks/useProgramaSituacao';
import {
  formatCurrencyView,
  handleCurrencyChange,
  unformatCurrency,
} from '../../utils/currencyUtils';

const OrcamentoModal = ({ show, onHide, orcamento, onSuccess }) => {
  const [formData, setFormData] = useState({
    ano: localStorage.getItem('anoBase'),
    id: '',
    idPrograma: '',
    idAcao: '',
    idSubAcao: null,
    idItem: null,
    cnpjAssociacao: localStorage.getItem('cnpj'),
    idElementoDespesa: '',
    valor: 'R$ 0,00',
  });

  const [programas, setProgramas] = useState([]);
  const [acoes, setAcoes] = useState([]);
  const [subAcoes, setSubAcoes] = useState([]);
  const [itens, setItens] = useState([]);
  const [elementosDespesa, setElementosDespesa] = useState([]);
  const [elementoAutoSelecionado, setElementoAutoSelecionado] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase = localStorage.getItem('anoBase');
  const { isProgramaAtivo } = useProgramaSituacao();
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  // Função para fechar o modal e limpar estados
  const handleClose = () => {
    setElementoAutoSelecionado(false);
    onHide();
  };

  // Reset form data quando o modal é fechado
  useEffect(() => {
    if (!show) {
      setFormData({
        ano: localStorage.getItem('anoBase'),
        id: '',
        idPrograma: '',
        idAcao: '',
        idSubAcao: null,
        idItem: null,
        cnpjAssociacao: localStorage.getItem('cnpj'),
        idElementoDespesa: '',
        valor: 'R$ 0,00',
      });
      setAcoes([]);
      setSubAcoes([]);
      setItens([]);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      const fetchInitialData = async () => {
        setIsLoading(true);
        try {
          const [programasRes, elementosRes] = await Promise.all([
            axios.get(`${API_BASE_URL.PROGRAMA}/all`, {
              params: { cnpjAssociacao: cnpj },
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API_BASE_URL.ELEMENTO_DESPESA}/all`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

          setProgramas(programasRes.data);
          setElementosDespesa(elementosRes.data);

          if (orcamento && orcamento.ano && orcamento.idPrograma) {
            // Está editando um orçamento existente
            setFormData({
              ...orcamento,
              valor: formatCurrencyView(orcamento.valor ?? 0),
            });

            if (orcamento.idPrograma) {
              await loadAcoes(orcamento.idPrograma);
            }
            if (orcamento.idPrograma && orcamento.idAcao) {
              await loadSubAcoes(orcamento.idPrograma, orcamento.idAcao);
              await loadItens(
                orcamento.idPrograma,
                orcamento.idAcao,
                orcamento.idSubAcao
              );
            }
          } else {
            // Novo orçamento - usar valores padrão
            setFormData({
              ano: localStorage.getItem('anoBase'),
              id: '',
              idPrograma: '',
              idAcao: '',
              idSubAcao: null,
              idItem: null,
              cnpjAssociacao: localStorage.getItem('cnpj'),
              idElementoDespesa: '',
              valor: 'R$ 0,00',
            });
            setElementoAutoSelecionado(false);
          }
        } catch (error) {
          console.error('Erro ao carregar dados:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchInitialData();
    }
  }, [show, orcamento]);

  // Atualiza o estado de programa inativo quando o programa selecionado mudar
  useEffect(() => {
    if (formData.idPrograma) {
      const programaAtivo = isProgramaAtivo(formData.idPrograma);
      setIsProgramaInativo(!programaAtivo);
    } else {
      setIsProgramaInativo(false);
    }
  }, [formData.idPrograma, isProgramaAtivo]);

  const loadAcoes = async idPrograma => {
    try {
      const response = await axios.get(`${API_BASE_URL.ACAO}/all`, {
        params: { cnpjAssociacao: cnpj, idPrograma },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAcoes(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar ações:', error);
      setAcoes([]);
      return [];
    }
  };

  const loadSubAcoes = async (idPrograma, idAcao) => {
    try {
      const response = await axios.get(`${API_BASE_URL.SUB_ACAO}/all`, {
        params: {
          cnpjAssociacao: cnpj,
          idPrograma,
          idAcao,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubAcoes(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar subações:', error);
      setSubAcoes([]);
      return [];
    }
  };

  const loadItens = async (idPrograma, idAcao, idSubAcao) => {
    if (!idPrograma || !idAcao) {
      setItens([]);
      return [];
    }

    try {
      let endpoint;
      let params;

      if (idSubAcao && idSubAcao !== '') {
        // Se há subação, usar endpoint específico
        endpoint = `${API_BASE_URL.ITEM}/subacao`;
        params = {
          cnpjAssociacao: cnpj,
          idPrograma,
          idAcao,
          idSubAcao,
        };
      } else {
        // Se não há subação, usar endpoint para ação
        endpoint = `${API_BASE_URL.ITEM}/acao`;
        params = {
          cnpjAssociacao: cnpj,
          idPrograma,
          idAcao,
        };
      }

      const response = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setItens(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
      setItens([]);
      return [];
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    let processedValue = value;

    if (
      [
        'idPrograma',
        'idAcao',
        'idSubAcao',
        'idElementoDespesa',
        'idItem',
      ].includes(name)
    ) {
      // Para campos opcionais, converter string vazia em null
      if (['idSubAcao', 'idItem'].includes(name)) {
        processedValue = value ? parseInt(value) : null;
      } else {
        processedValue = value ? parseInt(value) : '';
      }
    }

    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: processedValue,
      };

      // Se mudou o programa, carrega ações, limpa dependentes
      if (name === 'idPrograma') {
        setAcoes([]);
        setSubAcoes([]);
        setItens([]);
        setElementoAutoSelecionado(false);
        newFormData.idAcao = '';
        newFormData.idSubAcao = null;
        newFormData.idItem = null;
        if (value) {
          loadAcoes(value);
        }
      }
      // Se mudou a ação, carrega subações e itens, limpa dependentes
      if (name === 'idAcao') {
        setSubAcoes([]);
        setItens([]);
        setElementoAutoSelecionado(false);
        newFormData.idSubAcao = null;
        newFormData.idItem = null;
        if (newFormData.idPrograma && value) {
          loadSubAcoes(newFormData.idPrograma, value);
          loadItens(newFormData.idPrograma, value, null);
        }
      }
      // Se mudou a subação, carrega itens
      if (name === 'idSubAcao') {
        setItens([]);
        setElementoAutoSelecionado(false);
        newFormData.idItem = null;
        if (newFormData.idPrograma && newFormData.idAcao) {
          loadItens(newFormData.idPrograma, newFormData.idAcao, value);
        }
      }

      // Se mudou o item, auto-seleciona o elemento de despesa se o item já tiver um
      if (name === 'idItem') {
        if (value && itens.length > 0) {
          const itemSelecionado = itens.find(
            item => item && item.id && item.id === parseInt(value)
          );
          if (itemSelecionado && itemSelecionado.idElementoDespesa) {
            // Auto-popula o elemento de despesa do item selecionado
            newFormData.idElementoDespesa = itemSelecionado.idElementoDespesa;
            setElementoAutoSelecionado(true);
          } else {
            setElementoAutoSelecionado(false);
          }
        } else {
          // Se removeu a seleção do item, limpa a marcação
          setElementoAutoSelecionado(false);
        }
      }

      // Se mudou manualmente o elemento, remove a marcação de auto-selecionado
      if (name === 'idElementoDespesa') {
        setElementoAutoSelecionado(false);
      }

      return newFormData;
    });
  };

  const handleValorChange = e => {
    handleCurrencyChange(e, setFormData);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (formData.idPrograma && !isProgramaAtivo(formData.idPrograma)) {
      showDefaultModal('error', 'Este programa está ENCERRADO. Não é possível salvar orçamentos para programas encerrados.');
      return;
    }

    const payload = {
      ...formData,
      valor: unformatCurrency(formData.valor),
      cnpjAssociacao: cnpj,
      ano: anoBase,
      // Converter strings vazias em null para campos opcionais
      idSubAcao: formData.idSubAcao || null,
      idItem: formData.idItem || null,
    };

    try {
      const url = `${API_BASE_URL.ORCAMENTO}/save`;
      await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showDefaultModal('success', 'Orçamento salvo com sucesso!', () => {
        onSuccess();
        handleClose();
      });
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      const errorMessage =
        error.response?.data?.message ||
        'Erro ao salvar o orçamento. Tente novamente.';
      showDefaultModal('error', errorMessage);
    }
  };

  if (!show) return null;
  if (isLoading)
    return <div className="text-center p-3">Carregando dados...</div>;

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: '1000px', width: '95%' }}
      >
        <div className="modal-content">
          <div
            className="modal-header bg-success text-white"
            style={{ height: '30px' }}
          >
            <h5 className="modal-title">
              {orcamento && orcamento.ano
                ? 'Editar Orçamento'
                : 'Novo Orçamento'}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
            />
          </div>

          {isProgramaInativo && (
            <div className="alert alert-warning mt-2 mb-3 mx-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Este programa está <strong>ENCERRADO</strong>. Você pode visualizar os dados, mas não é possível salvar alterações.
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="modal-body small">
              <div className="row g-3">
                <div className="col-md-6 input-group-sm mb-1">
                  <label className="form-label">Programa</label>
                  <select
                    name="idPrograma"
                    className="form-select"
                    value={formData.idPrograma}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {programas
                      .filter(programa => programa && programa.id)
                      .map(programa => (
                        <option key={programa.id} value={programa.id}>
                          {programa.descricao}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-6 input-group-sm mb-1">
                  <label className="form-label">Ação</label>
                  <select
                    name="idAcao"
                    className="form-select"
                    value={formData.idAcao}
                    onChange={handleChange}
                    disabled={!formData.idPrograma}
                    required
                  >
                    <option value="">Selecione...</option>
                    {acoes
                      .filter(acao => acao && acao.id)
                      .map(acao => (
                        <option key={acao.id} value={acao.id}>
                          {acao.descricao}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-6 input-group-sm mb-1">
                  <label className="form-label">Sub-Ação</label>
                  <select
                    name="idSubAcao"
                    className="form-select"
                    value={formData.idSubAcao || ''}
                    onChange={handleChange}
                    disabled={!formData.idAcao || subAcoes.length === 0}
                    title="Caso não haja sub-ação, deixe em branco"
                  >
                    <option value="">Selecione...</option>
                    {subAcoes
                      .filter(sa => sa && sa.id)
                      .map(sa => (
                        <option key={sa.id} value={sa.id}>
                          {sa.descricao}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-6 input-group-sm mb-1">
                  <label className="form-label">Item</label>
                  <select
                    name="idItem"
                    className="form-select"
                    value={formData.idItem || ''}
                    onChange={handleChange}
                    disabled={!formData.idAcao || itens.length === 0}
                    title="Caso não haja item, deixe em branco"
                  >
                    <option value="">Selecione...</option>
                    {itens
                      .filter(item => item && item.id)
                      .map(item => (
                        <option key={item.id} value={item.id}>
                          {item.descricao}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-6 input-group-sm mb-1">
                  <label className="form-label">
                    Elemento da Despesa
                    {elementoAutoSelecionado && (
                      <small className="text-success ms-2">
                        <i className="bi bi-check-circle-fill me-1"></i>
                        (selecionado automaticamente)
                      </small>
                    )}
                  </label>
                  <select
                    name="idElementoDespesa"
                    className={`form-select ${
                      elementoAutoSelecionado ? 'border-success' : ''
                    }`}
                    value={formData.idElementoDespesa}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione...</option>
                    {elementosDespesa
                      .filter(elemento => elemento && elemento.id)
                      .map(elemento => (
                        <option key={elemento.id} value={elemento.id}>
                          {elemento.descricao}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-6 input-group-sm mb-1">
                  <label className="form-label">Valor</label>
                  <input
                    type="text"
                    className="form-control text-end"
                    value={formData.valor || 'R$ 0,00'}
                    onChange={handleValorChange}
                    onFocus={e => e.target.select()}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClose}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-success btn-sm" disabled={isProgramaInativo} title={isProgramaInativo ? 'Programa ENCERRADO - não é possível salvar' : undefined}>
                {orcamento && orcamento.ano ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
    </div>
  );
};

export default OrcamentoModal;
